import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SlidePanel from '../shared/SlidePanel';
import { refundService } from '../../_services/refund.service';
import {
  money, minusMoney, normalisePreview, refusalOf, buildRefundBody, parseAmount, amountError,
  selectedTotal, refundableTotal, makeIdempotencyKey, methodLineLabel, recipientLabel, feeLine,
  stockLine, loyaltyLine, sellerMessage, askLabel, PERMISSION_MESSAGE, REFUND_REASONS, afterLines,
  receiptLine, refundErrorText, LINE_KIND_LABEL, METHOD_LABEL, serverStockLine, methodsFromRows,
} from './refundLogic';
import './RefundPanel.css';

/**
 * Refund a completed sale (beu-node-v2 docs/refunds.md).
 *
 *   build   choose whole sale / items / an amount, see what happens, pick a reason
 *   review  the irreversible step: one primary button, "Refund £X"
 *   done    what to tell the client, receipt sent
 *   blocked the seller rule (a self-employed stylist's sale) or no permission
 *
 * Everything shown about money comes from POST /preview; the panel never
 * decides who pays for what. The server enforces seller, permission and site.
 */

const SPLIT_LABEL = { card: 'All to card', cash: 'All in cash', voucher: 'All to gift card', other: 'All as other' };

function Switch({ on, onChange, label }) {
  return (
    <button type="button" role="switch" aria-checked={!!on} aria-label={label} className={'rf-switch' + (on ? ' rf-switch--on' : '')} onClick={() => onChange(!on)}>
      <span className="rf-switch-knob" />
    </button>
  );
}

function Row({ label, value, strong, muted }) {
  return (
    <div className={'rf-row' + (strong ? ' rf-row--strong' : '') + (muted ? ' rf-row--muted' : '')}>
      <span className="rf-row-label">{label}</span>
      <span className="rf-row-value">{value}</span>
    </div>
  );
}

function Check({ on }) {
  return (
    <span className={'rf-check' + (on ? ' rf-check--on' : '')} aria-hidden="true">
      {on && (
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2.5 6.2L5 8.5L9.5 3.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
      )}
    </span>
  );
}

export default function RefundPanel({ bookingId, clientName, clientEmail, onClose, onRefunded, zIndex = 100020 }) {
  const panelRef = useRef(null);
  const mounted = useRef(true);
  const callerId = localStorage.getItem('userId');

  const [step, setStep] = useState('loading'); // loading | error | build | review | done | blocked
  const [loadError, setLoadError] = useState('');
  const [base, setBase] = useState(null);       // whole-sale preview: the sale's lines and how it was paid
  const [refusal, setRefusal] = useState(null);

  const [mode, setMode] = useState('whole');    // whole | lines | amount
  const [selected, setSelected] = useState({});
  const [restock, setRestock] = useState({});
  const [amountInput, setAmountInput] = useState('');
  const [split, setSplit] = useState('as_paid');

  const [preview, setPreview] = useState(null);
  const [previewKey, setPreviewKey] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const previewSeq = useRef(0);

  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const idemKey = useRef(null);
  const [result, setResult] = useState(null);
  const [done, setDone] = useState(null);       // what the preview said, frozen at the moment of refund

  const [askNote, setAskNote] = useState('');
  const [askState, setAskState] = useState('idle'); // idle | sending | sent
  const [askError, setAskError] = useState('');

  useEffect(() => () => { mounted.current = false; }, []);

  const close = () => {
    if (panelRef.current && panelRef.current.handleClose) panelRef.current.handleClose();
    else if (onClose) onClose();
  };

  // ── Load: preview of the whole sale ──
  const load = useCallback(async () => {
    setStep('loading');
    setLoadError('');
    try {
      const p = normalisePreview(await refundService.preview({ bookingId }));
      if (!mounted.current) return;
      setBase(p);
      setPreview(p);
      const rs = {};
      p.lines.forEach((l) => { if (l.kind === 'product') rs[l.key] = l.restockable; });
      setRestock(rs);
      // Stripe couldn't be checked just now: that's a try-again, not a refusal.
      if (!p.allowed && p.reasonCode === 'stripe_unavailable') {
        setLoadError(p.reasonText || "Couldn't check the card payment just now. Try again in a moment.");
        setStep('error');
        return;
      }
      const r = refusalOf(p, callerId);
      if (r) { setRefusal({ ...r, canRequest: p.canRequest }); setStep('blocked'); } else setStep('build');
    } catch (e) {
      if (!mounted.current) return;
      // A refusal can arrive as an error envelope too ({ code, seller } in
      // error.errors); read it the same way. Anything else is a try-again.
      const detail = (e && e.detail) || {};
      if (e && e.fromServer && e.code !== 'stripe_unavailable') {
        const r = refusalOf(normalisePreview({ allowed: false, code: e.code, reason: e.message, seller: detail.seller, canRequest: detail.canRequest }), callerId);
        setRefusal({ ...(r && r.kind !== 'other' ? r : { kind: 'other', text: refundErrorText(e) }), canRequest: detail.canRequest !== false });
        setStep('blocked');
        return;
      }
      setLoadError(refundErrorText(e, "Couldn't load this sale. Please try again."));
      setStep('error');
    }
  }, [bookingId, callerId]);

  useEffect(() => { load(); }, [load]);

  // ── What's being refunded ──
  const lines = useMemo(() => (base ? base.lines : []), [base]);
  const refundable = base ? (base.refundable != null ? base.refundable : refundableTotal(lines)) : 0;
  const parsedAmount = parseAmount(amountInput);
  const amountMsg = mode === 'amount' && amountInput !== '' ? amountError(parsedAmount, refundable) : '';
  const anySelected = Object.keys(selected).some((k) => selected[k]);

  const localAmount = mode === 'amount' ? (amountMsg ? 0 : (parsedAmount || 0))
    : mode === 'lines' ? selectedTotal(lines, selected)
      : (base && base.amount > 0 ? base.amount : refundableTotal(lines)); // whole: the server's own total for 'everything left'

  const payMethods = base ? (base.paidMethods.length ? base.paidMethods : base.methods) : [];
  const splitOptions = payMethods.length > 1 ? payMethods.map((m) => m.method).filter((m, i, a) => a.indexOf(m) === i) : [];

  const body = useMemo(() => {
    if (!base || step === 'loading' || step === 'blocked' || step === 'error') return null;
    if (mode === 'lines' && !anySelected) return null;
    if (mode === 'amount' && (!parsedAmount || amountError(parsedAmount, refundable))) return null;
    const methodSplit = split !== 'as_paid' && localAmount > 0 ? { [split]: localAmount } : null;
    return buildRefundBody({ bookingId, mode, lines, selected, restock, amount: parsedAmount, methodSplit });
  }, [base, step, mode, anySelected, parsedAmount, refundable, split, localAmount, bookingId, lines, selected, restock]);
  const bodyKey = body ? JSON.stringify(body) : '';

  // ── Live preview for the current choice (debounced; stale replies dropped) ──
  useEffect(() => {
    if (step !== 'build') return undefined;
    const seq = ++previewSeq.current;
    if (!bodyKey) { setPreviewLoading(false); setPreviewError(''); return undefined; }
    setPreviewLoading(true);
    setPreviewError('');
    const t = setTimeout(async () => {
      try {
        const p = normalisePreview(await refundService.preview(JSON.parse(bodyKey)));
        if (!mounted.current || seq !== previewSeq.current) return;
        setPreview(p);
        setPreviewKey(bodyKey);
        setPreviewError(p.allowed ? '' : (p.reasonText || 'This refund can\'t go ahead as it is.'));
      } catch (e) {
        if (!mounted.current || seq !== previewSeq.current) return;
        setPreviewKey('');
        setPreviewError(refundErrorText(e, "Couldn't work out this refund. Please try again."));
      } finally {
        if (mounted.current && seq === previewSeq.current) setPreviewLoading(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [bodyKey, step]);

  const fresh = !!preview && previewKey === bodyKey && !!bodyKey && !previewLoading;
  const shownAmount = fresh && preview.amount > 0 ? preview.amount : localAmount;
  const canContinue = fresh && preview.allowed && shownAmount > 0 && !!reason && !previewError;

  // ── Actions ──
  const toggleLine = (l) => {
    if (!(l.refundable > 0)) return;
    setSelected((s) => ({ ...s, [l.key]: !s[l.key] }));
  };

  const goReview = () => {
    if (!canContinue) return;
    idemKey.current = makeIdempotencyKey(bookingId); // one key per attempt: a retry can't refund twice
    setSubmitError('');
    setStep('review');
  };

  const submit = async () => {
    if (submitting || !body) return;
    setSubmitting(true);
    setSubmitError('');
    const payload = { ...body, reason, idempotencyKey: idemKey.current };
    if (note.trim()) payload.note = note.trim();
    const frozen = { amount: shownAmount, methods: preview ? preview.methods : [] };
    try {
      const res = (await refundService.create(payload)) || {};
      if (onRefunded) onRefunded(res);
      if (!mounted.current) return;
      setResult(res);
      const actual = methodsFromRows(res.rows || (res.refund ? [res.refund] : null));
      setDone(actual.length ? { amount: actual.reduce((t, m) => t + m.amount, 0), methods: actual } : frozen);
      setStep('done');
    } catch (e) {
      if (!mounted.current) return;
      // Nothing was refunded (the server says so): the next try is a new
      // attempt. Anything else keeps the key, so a retry resumes and can
      // never refund twice.
      if (e && e.fromServer && e.code === 'refund_failed') idemKey.current = makeIdempotencyKey(bookingId);
      setSubmitError(refundErrorText(e, "The refund didn't go through. Nothing was refunded. Please try again."));
    } finally {
      if (mounted.current) setSubmitting(false);
    }
  };

  const ask = async () => {
    if (askState === 'sending') return;
    setAskState('sending');
    setAskError('');
    try {
      const b = { bookingId };
      if (askNote.trim()) b.note = askNote.trim();
      await refundService.request(b);
      if (mounted.current) setAskState('sent');
    } catch (e) {
      if (!mounted.current) return;
      setAskState('idle');
      setAskError(refundErrorText(e, "Couldn't send the request. Please try again."));
    }
  };

  // ── Pieces ──
  const saleTotal = base && base.total > 0 ? base.total : lines.reduce((s, l) => s + l.paid, 0);
  const saleHeader = base && (
    <div className="rf-sale">
      <div className="rf-sale-who">{clientName || 'Walk-in'}</div>
      <div className="rf-sale-meta">
        {saleTotal > 0 ? `Paid ${money(saleTotal)}` : 'Completed sale'}
        {base.alreadyRefunded > 0 ? ` · ${money(base.alreadyRefunded)} already refunded` : ''}
      </div>
    </div>
  );

  const renderPreview = () => {
    const p = preview;
    if (!p) return null;
    const methods = fresh ? p.methods : [];
    const from = fresh ? p.recipients : [];
    const notes = [fresh ? feeLine(p.fee) : '', (fresh && p.stock ? serverStockLine(p.stock) : stockLine(lines, restock, mode, selected)), fresh ? loyaltyLine(p.loyalty) : ''].filter(Boolean);
    return (
      <div className={'rf-section rf-preview' + (previewLoading ? ' rf-preview--loading' : '')} aria-live="polite">
        <div className="rf-section-title">What happens</div>
        {previewError ? (
          <div className="rf-calm">{previewError}</div>
        ) : !fresh ? (
          <div className="rf-preview-wait">{bodyKey ? 'Working it out…' : (mode === 'lines' ? 'Choose what to refund.' : 'Enter an amount.')}</div>
        ) : (
          <>
            {methods.length > 0 && (
              <div className="rf-group">
                <div className="rf-group-label">Back to the client</div>
                {methods.map((m, i) => <Row key={i} label={methodLineLabel(m)} value={money(m.amount)} />)}
              </div>
            )}
            {from.length > 0 && (
              <div className="rf-group">
                <div className="rf-group-label">Comes back from</div>
                {from.map((r, i) => <Row key={i} label={recipientLabel(r)} value={money(r.amount)} />)}
              </div>
            )}
            {notes.map((n) => <p key={n} className="rf-note">{n}</p>)}
          </>
        )}
      </div>
    );
  };

  const renderBuild = () => {
    const products = lines.filter((l) => l.kind === 'product' && l.refundable > 0 && l.restockable);
    return (
      <div className="rf">
        {saleHeader}

        <div className="rf-seg" role="tablist" aria-label="What to refund">
          {[['whole', 'Whole sale'], ...(lines.length > 1 ? [['lines', 'Items']] : []), ['amount', 'Amount']].map(([id, label]) => (
            <button key={id} type="button" role="tab" aria-selected={mode === id} className={'rf-seg-btn' + (mode === id ? ' rf-seg-btn--on' : '')} onClick={() => setMode(id)}>{label}</button>
          ))}
        </div>

        {mode === 'whole' && (
          <div className="rf-section">
            <Row label="Refund everything left" value={money(localAmount)} strong />
            {lines.length > 0 && <p className="rf-hint">{lines.filter((l) => l.refundable > 0).map((l) => l.name).join(', ')}</p>}
          </div>
        )}

        {mode === 'lines' && (
          <div className="rf-section">
            <div className="rf-lines">
              {lines.map((l) => {
                const on = !!selected[l.key];
                const gone = !(l.refundable > 0);
                const goneLabel = l.comped ? 'Loyalty reward' : 'Refunded';
                return (
                  <div key={l.key} className={'rf-line' + (gone ? ' rf-line--gone' : '')}>
                    <button type="button" className="rf-line-main" onClick={() => toggleLine(l)} disabled={gone} aria-pressed={on}>
                      <Check on={on} />
                      <span className="rf-line-text">
                        <span className="rf-line-name">{l.name}{l.qty > 1 ? ` × ${l.qty}` : ''}</span>
                        <span className="rf-line-kind">
                          {LINE_KIND_LABEL[l.kind] || 'Item'}
                          {l.refunded > 0 && !gone ? ` · ${money(l.refunded)} already refunded` : ''}
                        </span>
                      </span>
                      <span className="rf-line-amount">{gone ? goneLabel : money(l.refundable)}</span>
                    </button>
                    {on && l.kind === 'product' && l.restockable && (
                      <div className="rf-line-sub">
                        <span>Put back in stock</span>
                        <Switch on={!!restock[l.key]} onChange={(v) => setRestock((r) => ({ ...r, [l.key]: v }))} label={`Put ${l.name} back in stock`} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {mode === 'whole' && products.length > 0 && (
          <div className="rf-section">
            <div className="rf-section-title">Stock</div>
            {products.map((l) => (
              <div key={l.key} className="rf-toggle-row">
                <span>Put {l.name}{l.qty > 1 ? ` × ${l.qty}` : ''} back in stock</span>
                <Switch on={!!restock[l.key]} onChange={(v) => setRestock((r) => ({ ...r, [l.key]: v }))} label={`Put ${l.name} back in stock`} />
              </div>
            ))}
          </div>
        )}

        {mode === 'amount' && (
          <div className="rf-section">
            <label className="rf-amount" htmlFor="rf-amount-input">
              <span className="rf-amount-currency">£</span>
              <input
                id="rf-amount-input"
                className="rf-amount-input"
                inputMode="decimal"
                autoComplete="off"
                placeholder="0.00"
                value={amountInput}
                onChange={(e) => setAmountInput(e.target.value)}
              />
            </label>
            {amountMsg ? <p className="rf-field-error">{amountMsg}</p> : <p className="rf-hint">Up to {money(refundable)}</p>}
          </div>
        )}

        {splitOptions.length > 0 && (
          <div className="rf-section">
            <div className="rf-section-title">Pay it back</div>
            <div className="rf-chips">
              {['as_paid', ...splitOptions].map((m) => (
                <button key={m} type="button" className={'rf-chip' + (split === m ? ' rf-chip--on' : '')} onClick={() => setSplit(m)}>
                  {m === 'as_paid' ? 'The way it was paid' : (SPLIT_LABEL[m] || `All to ${(METHOD_LABEL[m] || m).toLowerCase()}`)}
                </button>
              ))}
            </div>
          </div>
        )}

        {renderPreview()}

        <div className="rf-section">
          <div className="rf-section-title">Reason</div>
          <div className="rf-chips">
            {REFUND_REASONS.map((r) => (
              <button key={r} type="button" className={'rf-chip' + (reason === r ? ' rf-chip--on' : '')} onClick={() => setReason(r)}>{r}</button>
            ))}
          </div>
          <textarea className="sp-textarea rf-textarea" rows={2} placeholder="Add a note (optional)" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} />
        </div>
      </div>
    );
  };

  const renderReview = () => {
    const p = preview || {};
    const notes = [feeLine(p.fee), p.stock ? serverStockLine(p.stock) : stockLine(lines, restock, mode, selected), loyaltyLine(p.loyalty)].filter(Boolean);
    return (
      <div className="rf">
        <div className="rf-hero">
          <div className="rf-hero-amount">{money(shownAmount)}</div>
          <div className="rf-hero-sub">back to {clientName || 'the client'}</div>
        </div>
        <div className="rf-section">
          {(p.methods || []).map((m, i) => <Row key={`m${i}`} label={methodLineLabel(m)} value={money(m.amount)} />)}
          {(p.recipients || []).length > 0 && <div className="rf-divider" />}
          {(p.recipients || []).map((r, i) => <Row key={`r${i}`} label={`From ${r.role === 'salon' ? 'the salon' : (r.name || 'the stylist')}`} value={minusMoney(r.amount)} muted />)}
          <div className="rf-divider" />
          <Row label="Reason" value={reason} />
          {note.trim() && <p className="rf-note">{note.trim()}</p>}
          {notes.map((n) => <p key={n} className="rf-note">{n}</p>)}
        </div>
        <p className="rf-final">This can't be undone.</p>
        {submitError && <div className="rf-calm rf-calm--error" role="alert">{submitError}</div>}
      </div>
    );
  };

  const renderDone = () => {
    const rows = (result && (result.rows || (result.refund ? [result.refund] : []))) || [];
    const pending = rows.some((r) => r && String(r.status || '').toLowerCase() === 'pending');
    const receipt = receiptLine(result, clientEmail);
    return (
      <div className="rf rf-done">
        <div className="rf-done-icon" aria-hidden="true">
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none"><path d="M5 12.5L10 17.5L19 7.5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </div>
        <div className="rf-done-title">{money(done ? done.amount : shownAmount)} refunded</div>
        {pending && <p className="rf-done-line">The card refund is on its way. It shows as refunded once the card company confirms.</p>}
        {afterLines(done ? done.methods : []).map((l) => <p key={l} className="rf-done-line">{l}</p>)}
        {receipt && <p className="rf-done-receipt">{receipt}</p>}
      </div>
    );
  };

  const renderBlocked = () => {
    const r = refusal || {};
    let title = "This sale can't be refunded";
    let text = r.text || '';
    if (r.kind === 'seller') { title = r.name ? `Only ${r.name} can refund this` : 'Only the stylist can refund this'; text = sellerMessage(r.name); }
    if (r.kind === 'permission') { title = "You can't refund this sale"; text = PERMISSION_MESSAGE; }
    if (r.kind === 'nothing') { title = 'Nothing left to refund'; text = 'This sale has already been refunded in full.'; }
    return (
      <div className="rf rf-blocked">
        {saleHeader}
        <div className="rf-blocked-card">
          <div className="rf-blocked-title">{title}</div>
          <p className="rf-blocked-text">{text}</p>
        </div>
        {r.kind === 'seller' && r.canRequest !== false && (
          askState === 'sent' ? (
            <div className="rf-calm rf-calm--ok">Request sent. {r.name || 'The stylist'} can see it now.</div>
          ) : (
            <div className="rf-section">
              <div className="rf-section-title">Ask them</div>
              <textarea className="sp-textarea rf-textarea" rows={2} placeholder={`A note for ${r.name || 'them'} (optional)`} value={askNote} onChange={(e) => setAskNote(e.target.value)} maxLength={500} />
              {askError && <div className="rf-calm rf-calm--error" role="alert">{askError}</div>}
            </div>
          )
        )}
      </div>
    );
  };

  let content = null;
  let footer = null;
  let title = 'Refund';
  if (step === 'loading') {
    content = (
      <div className="rf">
        <div className="rf-skel" style={{ width: '45%', height: 18 }} />
        <div className="rf-skel" style={{ width: '65%', height: 12, marginTop: 10 }} />
        <div className="rf-skel" style={{ width: '100%', height: 36, marginTop: 24 }} />
        <div className="rf-skel" style={{ width: '100%', height: 120, marginTop: 20 }} />
      </div>
    );
  } else if (step === 'error') {
    content = <div className="rf"><div className="rf-calm" role="alert">{loadError}</div></div>;
    footer = <button type="button" className="sp-btn-primary" onClick={load}>Try again</button>;
  } else if (step === 'build') {
    content = renderBuild();
    footer = (
      <>
        <button type="button" className="sp-btn-secondary" onClick={close}>Cancel</button>
        <button type="button" className="sp-btn-primary" disabled={!canContinue} onClick={goReview}>
          {!reason && fresh && preview && preview.allowed && !previewError ? 'Choose a reason' : 'Continue'}
        </button>
      </>
    );
  } else if (step === 'review') {
    title = 'Confirm refund';
    content = renderReview();
    footer = (
      <>
        <button type="button" className="sp-btn-secondary" onClick={() => setStep('build')} disabled={submitting}>Back</button>
        <button type="button" className="sp-btn-primary rf-btn-refund" onClick={submit} disabled={submitting}>
          {submitting ? 'Refunding…' : `Refund ${money(shownAmount)}`}
        </button>
      </>
    );
  } else if (step === 'done') {
    title = 'Refund';
    content = renderDone();
    footer = <button type="button" className="sp-btn-primary" onClick={close}>Done</button>;
  } else if (step === 'blocked') {
    content = renderBlocked();
    const r = refusal || {};
    footer = r.kind === 'seller' && r.canRequest !== false && askState !== 'sent' ? (
      <button type="button" className="sp-btn-secondary rf-btn-ask" onClick={ask} disabled={askState === 'sending'}>
        {askState === 'sending' ? 'Sending…' : askLabel(r.name)}
      </button>
    ) : (
      <button type="button" className="sp-btn-secondary" onClick={close}>Close</button>
    );
  }

  return (
    <SlidePanel ref={panelRef} title={title} onClose={onClose} footer={footer} width={460} zIndex={zIndex} className="rf-panel">
      {content}
    </SlidePanel>
  );
}
